import i18next from "i18next";

import { formatShortDateWithWeekday, formatTime } from "@ergon-ai/utils";

/**
 * Formats a date for display in chat message timestamps.
 *
 * - Returns `"Today 08:37"` if the date is the current calendar day.
 * - Returns `"Yesterday 14:22"` if the date is the previous calendar day.
 * - Returns `"lunedì 27/01/25 08:35"` (weekday + short date + time) for older dates.
 *
 * Uses i18n for "Today"/"Yesterday" labels and Intl.DateTimeFormat for locale-aware formatting.
 *
 * @param date - The date to format (ISO string or Date object).
 * @param locale - One of the six supported tags (e.g. `"it-IT"`, `"en-GB"`).
 * Defaults to the current i18next language. It is used BOTH for Intl date
 * formatting and as the i18next resource key, and those two accept different
 * things: a bare `"it"` or an `"it-CH"` formats fine but has no resource
 * entry, so the relative word would silently come back in the fallback
 * language. Pass an exact supported tag.
 * @returns A formatted chat timestamp string.
 *
 * @example
 * ```ts
 * getChatTimestamp("2026-02-25T08:37:00Z", "it-IT"); // "Oggi 08:37"
 * getChatTimestamp("2026-02-24T14:22:00Z", "it-IT"); // "Ieri 14:22"
 * getChatTimestamp("2026-02-20T08:35:00Z", "it-IT"); // "giovedì 20/02/26 08:35"
 * ```
 */
export const getChatTimestamp = (
  date: Date | string,
  locale?: string,
): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const resolvedLocale = locale ?? i18next.language;
  const time = formatTime(dateObj, resolvedLocale);

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const inputDay = new Date(
    dateObj.getFullYear(),
    dateObj.getMonth(),
    dateObj.getDate(),
  );

  if (inputDay.getTime() === today.getTime()) {
    // `lng` explicitly: see getChatDateLabel (EAH-2135).
    return `${i18next.t("date.today", { lng: resolvedLocale })} ${time}`;
  }

  if (inputDay.getTime() === yesterday.getTime()) {
    return `${i18next.t("date.yesterday", { lng: resolvedLocale })} ${time}`;
  }

  // Older: "lunedì 27/01/25 08:35" or "Monday 27/01/25 08:35"
  const dateStr = formatShortDateWithWeekday(dateObj, resolvedLocale);
  return `${dateStr} ${time}`;
};
